// src/pages/PhoneModels.js
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useParams, Link, useLocation, useNavigate } from 'react-router-dom';
import { FaTools, FaSearch } from 'react-icons/fa';
import RepairsModal from '../components/RepairsModal';

function PhoneModels() {
    const { brand: brandUrlParam } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [searchTerm, setSearchTerm] = useState('');
    const [models, setModels] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [selectedPhone, setSelectedPhone] = useState(null);
    const [repairsData, setRepairsData] = useState([]);
    const [showRepairsModal, setShowRepairsModal] = useState(false);
    const [isLoadingRepairs, setIsLoadingRepairs] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [hasOpenedModalFromUrl, setHasOpenedModalFromUrl] = useState(false);

    const getPhoneDbBrand = useCallback((urlBrand) => {
        const lower = urlBrand?.toLowerCase();
        switch (lower) {
            case 'iphone': return 'Apple';
            case 'samsung': return 'Samsung';
            case 'pixel': return 'Google Pixel';
            case 'oneplus': return 'OnePlus';
            case 'huawei': return 'Huawei';
            case 'nokia': return 'Nokia';
            case 'motorola': return 'Motorola';
            case 'oppo': return 'Oppo';
            case 'redmi': return 'Redmi';
            case 'xiaomi': return 'Redmi'; // Xiaomi and Redmi are the same brand group
            default: return urlBrand;
        }
    }, []);

    // Function to extract or determine phone release year
    const getPhoneReleaseYear = useCallback((phoneName, brand) => {
        const name = phoneName.toLowerCase();
        
        // Phone model to year mapping for known models
        const phoneYearMapping = {
            // iPhone models
            'iphone 16 pro max': 2024, 'iphone 16 pro': 2024, 'iphone 16 plus': 2024, 'iphone 16': 2024,
            'iphone 15 pro max': 2023, 'iphone 15 pro': 2023, 'iphone 15 plus': 2023, 'iphone 15': 2023,
            'iphone 14 pro max': 2022, 'iphone 14 pro': 2022, 'iphone 14 plus': 2022, 'iphone 14': 2022,
            'iphone 13 pro max': 2021, 'iphone 13 pro': 2021, 'iphone 13 mini': 2021, 'iphone 13': 2021,
            'iphone 12 pro max': 2020, 'iphone 12 pro': 2020, 'iphone 12 mini': 2020, 'iphone 12': 2020,
            'iphone se 3 (2022)': 2022, 'iphone se (3rd generation)': 2022, 'iphone se 3rd generation': 2022,
            'iphone se (2nd generation)': 2020, 'iphone se 2nd generation': 2020, 'iphone se (2020)': 2020,
            'iphone 11 pro max': 2019, 'iphone 11 pro': 2019, 'iphone 11': 2019,
            'iphone xs max': 2018, 'iphone xs': 2018, 'iphone xr': 2018,
            'iphone x': 2017, 'iphone 8 plus': 2017, 'iphone 8': 2017,
            'iphone 7 plus': 2016, 'iphone 7': 2016, 'iphone 6s plus': 2015, 'iphone 6s': 2015,
            'iphone 6 plus': 2014, 'iphone 6': 2014, 'iphone 5c': 2013,
            
            // Samsung Galaxy S Series
            'galaxy s24 ultra': 2024, 'galaxy s24 plus': 2024, 'galaxy s24': 2024,
            'galaxy s23 ultra': 2023, 'galaxy s23 plus': 2023, 'galaxy s23': 2023,
            'galaxy s22 ultra': 2022, 'galaxy s22 plus': 2022, 'galaxy s22': 2022,
            'galaxy s21 ultra': 2021, 'galaxy s21 plus': 2021, 'galaxy s21': 2021, 'galaxy s21 fe': 2021,
            'galaxy s20 ultra': 2020, 'galaxy s20 plus': 2020, 'galaxy s20': 2020, 'galaxy s20 fe': 2020,
            'galaxy s10 5g': 2019, 'galaxy s10 plus': 2019, 'galaxy s10': 2019, 'galaxy s10e': 2019, 'galaxy s10 lite': 2019,
            'galaxy s9 plus': 2018, 'galaxy s9': 2018, 'galaxy s8 plus': 2017, 'galaxy s8': 2017,
            'galaxy s7 edge': 2016, 'galaxy s7': 2016, 'galaxy s6 edge': 2015, 'galaxy s6': 2015,
            
            // Samsung Galaxy Z Series
            'samsung z flip 6': 2024, 'samsung z fold 6': 2024,
            'samsung z flip 5': 2023, 'samsung z fold 5': 2023,
            'samsung z flip 4': 2022, 'samsung z fold 4': 2022,
            'samsung z flip 3': 2021, 'samsung z fold 3': 2021,
            'samsung z flip 2': 2020, 'samsung z fold 2': 2020,
            'samsung z flip': 2020, 'samsung z fold': 2019,
            
            // Samsung Galaxy A Series
            'samsung a55': 2024, 'samsung a35': 2024, 'samsung a25': 2024, 'samsung a15': 2024, 'samsung a15 5g': 2024,
            'samsung a54': 2023, 'samsung a34': 2023, 'samsung a24': 2023, 'samsung a14': 2023,
            'samsung a53': 2022, 'samsung a33': 2022, 'samsung a23': 2022, 'samsung a13': 2022,
            'samsung a52': 2021, 'samsung a52s 5g': 2021, 'samsung a32': 2021, 'samsung a22': 2021, 'samsung a12': 2021,
            'samsung a51': 2020, 'samsung a31': 2020, 'samsung a21': 2020, 'samsung a21s': 2020, 'samsung a11': 2020,
            'samsung a50': 2019, 'samsung a40': 2019, 'samsung a30': 2019, 'samsung a20': 2019, 'samsung a20s': 2019, 'samsung a10s': 2019,
            
            // Samsung Galaxy Note Series
            'galaxy note 20 ultra': 2020, 'galaxy note 20': 2020,
            'galaxy note 10 plus': 2019, 'galaxy note 10': 2019, 'galaxy note 10 lite': 2020,
            'galaxy note 9': 2018, 'galaxy note 8': 2017,
            
            // Samsung Galaxy J Series
            'samsung galaxy j6': 2018, 'samsung galaxy j5 2017': 2017, 'samsung galaxy j5': 2015,
            'samsung galaxy j4': 2018, 'samsung galaxy j3 2017': 2017, 'samsung galaxy j3': 2016,
            
            // Google Pixel
            'pixel 9 pro xl': 2024, 'pixel 9 pro': 2024, 'pixel 9': 2024,
            'pixel 8a': 2024, 'pixel 8 pro': 2023, 'pixel 8': 2023,
            'pixel 7a': 2023, 'pixel 7 pro': 2022, 'pixel 7': 2022,
            'pixel 6a': 2022, 'pixel 6 pro': 2021, 'pixel 6': 2021,
            'pixel 5a': 2021, 'pixel 5': 2020, 'pixel 4a 5g': 2020, 'pixel 4a': 2020,
            'pixel 4 xl': 2019, 'pixel 4': 2019, 'pixel 3a xl': 2019, 'pixel 3a': 2019,
            'pixel 3 xl': 2018, 'pixel 3': 2018, 'pixel 2 xl': 2017, 'pixel 2': 2017,
            
            // OnePlus
            'oneplus 12': 2024, 'oneplus 12r': 2024,
            'oneplus 11': 2023, 'oneplus 11r': 2023,
            'oneplus 10 pro': 2022, 'oneplus 10t': 2022,
            'oneplus 9 pro': 2021, 'oneplus 9': 2021, 'oneplus 9r': 2021,
            'oneplus nord': 2020, 'oneplus nord n10 5g': 2020, 'oneplus nord 5g': 2020,
            'oneplus 8t': 2020, 'oneplus 8 pro': 2020, 'oneplus 8': 2020,
            'oneplus 7t pro 5g': 2019, 'oneplus 7t': 2019, 'oneplus 7 pro': 2019, 'oneplus 7': 2019,
            'oneplus 6t mclaren': 2018, 'oneplus 6t': 2018, 'oneplus 6': 2018,
            'oneplus 5t': 2017, 'oneplus 5': 2017,
            'oneplus 3t': 2016, 'oneplus 3': 2016,
            'oneplus one': 2014,
            
            // Nokia
            'nokia xr21': 2023, 'nokia x30 5g': 2022, 'nokia xr20': 2021,
            'nokia g60 5g': 2022, 'nokia g50': 2021, 'nokia g42 5g': 2023,
            'nokia g22': 2023, 'nokia g21': 2022, 'nokia g20': 2021,
            'nokia g11': 2022, 'nokia g10': 2021,
            'nokia c32': 2023, 'nokia c31': 2022, 'nokia c21 plus': 2022,
            'nokia c21': 2022, 'nokia c20': 2021, 'nokia c12': 2023, 'nokia c10': 2021,
            'nokia 8.3 5g': 2020, 'nokia 7.2': 2019, 'nokia 6.2': 2019,
            'nokia 5.4': 2020, 'nokia 5.3': 2020, 'nokia 4.2': 2019,
            'nokia 3.4': 2020, 'nokia 2.4': 2020, 'nokia 1.4': 2021,
            'nokia 9 pureview': 2019, 'nokia 8 sirocco': 2018,
            'nokia 8.1': 2018, 'nokia 7 plus': 2018,
            
            // Motorola
            'motorola edge 40 pro': 2023, 'motorola edge 40': 2023, 'motorola edge 30 ultra': 2022,
            'motorola edge 30 pro': 2022, 'motorola edge 30': 2022, 'motorola edge 20 pro': 2021,
            'motorola edge 20': 2021, 'motorola edge plus': 2020, 'motorola edge': 2020,
            'moto g84 5g': 2023, 'moto g73': 2023, 'moto g72': 2022,
            'moto g62 5g': 2022, 'moto g52': 2022, 'moto g51 5g': 2021,
            'moto g50': 2021, 'moto g41': 2021, 'moto g31': 2021,
            'moto g30': 2021, 'moto g10': 2021, 'moto g9 plus': 2020,
            'moto g9 power': 2020, 'moto g9': 2020, 'moto g8 power': 2020,
            'moto g8': 2020, 'moto g7 power': 2019, 'moto g7': 2019,
            
            // Huawei
            'huawei p60 pro': 2023, 'huawei p60': 2023, 'huawei p50 pro': 2021,
            'huawei p50': 2021, 'huawei p40 pro': 2020, 'huawei p40': 2020,
            'huawei p30 pro': 2019, 'huawei p30': 2019, 'huawei p20 pro': 2018,
            'huawei p20': 2018, 'huawei mate 50 pro': 2022, 'huawei mate 50': 2022,
            'huawei mate 40 pro': 2020, 'huawei mate 40': 2020,
            'huawei nova 11 pro': 2023, 'huawei nova 11': 2023, 'huawei nova 10 pro': 2022,
            'huawei nova 10': 2022, 'huawei nova 9': 2021, 'huawei nova 8': 2021,
            
            // Oppo
            'oppo find x6 pro': 2023, 'oppo find x6': 2023, 'oppo find x5 pro': 2022,
            'oppo find x5': 2022, 'oppo find x3 pro': 2021, 'oppo find x3': 2021,
            'oppo reno 10 pro': 2023, 'oppo reno 10': 2023, 'oppo reno 9 pro': 2022,
            'oppo reno 9': 2022, 'oppo reno 8 pro': 2022, 'oppo reno 8': 2022,
            'oppo reno 7': 2021, 'oppo reno 6': 2021, 'oppo reno 5': 2020,
            'oppo a98 5g': 2023, 'oppo a78 5g': 2023, 'oppo a77': 2022,
            'oppo a57': 2022, 'oppo a55': 2021, 'oppo a54': 2021,
            
            // Xiaomi/Redmi
            'xiaomi 13 ultra': 2023, 'xiaomi 13 pro': 2023, 'xiaomi 13': 2023,
            'xiaomi 12 pro': 2022, 'xiaomi 12': 2022, 'xiaomi 11': 2021,
            'redmi note 12 pro': 2023, 'redmi note 12': 2023, 'redmi note 11 pro': 2022,
            'redmi note 11': 2022, 'redmi note 10 pro': 2021, 'redmi note 10': 2021,
            'redmi 12': 2023, 'redmi 11': 2022, 'redmi 10': 2021,
            'poco x5 pro': 2023, 'poco x5': 2023, 'poco x4 pro': 2022,
            'poco x4': 2022, 'poco x3 pro': 2021, 'poco x3': 2020,
        };
        
        // Check exact match first
        const exactMatch = phoneYearMapping[name];
        if (exactMatch) return exactMatch;
        
        // Try partial matching for variations
        for (const [modelName, year] of Object.entries(phoneYearMapping)) {
            if (name.includes(modelName) || modelName.includes(name)) {
                return year;
            }
        }
        
        // Extract year from phone name (look for 4-digit years)
        const yearMatch = name.match(/\b(20[0-9]{2})\b/);
        if (yearMatch) {
            return parseInt(yearMatch[1]);
        }
        
        // Extract number-based models and estimate year
        if (brand === 'Apple') {
            const iphoneMatch = name.match(/iphone\s*(\d+)/);
            if (iphoneMatch) {
                const modelNum = parseInt(iphoneMatch[1]);
                if (modelNum >= 16) return 2024;
                if (modelNum >= 15) return 2023;
                if (modelNum >= 14) return 2022;
                if (modelNum >= 13) return 2021;
                if (modelNum >= 12) return 2020;
                if (modelNum >= 11) return 2019;
                if (modelNum >= 10) return 2017;
                if (modelNum >= 8) return 2017;
                if (modelNum >= 7) return 2016;
                if (modelNum >= 6) return 2014;
            }
        }
        
        if (brand === 'Samsung') {
            const sMatch = name.match(/galaxy\s*s\s*(\d+)/);
            if (sMatch) {
                const modelNum = parseInt(sMatch[1]);
                if (modelNum >= 24) return 2024;
                if (modelNum >= 23) return 2023;
                if (modelNum >= 22) return 2022;
                if (modelNum >= 21) return 2021;
                if (modelNum >= 20) return 2020;
                if (modelNum >= 10) return 2019;
                if (modelNum >= 9) return 2018;
                if (modelNum >= 8) return 2017;
                if (modelNum >= 7) return 2016;
                if (modelNum >= 6) return 2015;
            }
            
            const aMatch = name.match(/a\s*(\d+)/);
            if (aMatch) {
                const modelNum = parseInt(aMatch[1]);
                if (modelNum >= 50) return 2019 + Math.floor((modelNum - 50) / 10);
                if (modelNum >= 30) return 2019;
                if (modelNum >= 10) return 2019 + Math.floor((modelNum - 10) / 10);
            }
        }
        
        // Default to 2020 if year cannot be determined
        return 2020;
    }, []);

    // Function to sort phones by year (latest first)
    const sortPhonesByYear = useCallback((phones) => {
        return [...phones].sort((a, b) => {
            const yearA = getPhoneReleaseYear(a.model || a.name, a.brand);
            const yearB = getPhoneReleaseYear(b.model || b.name, b.brand);
            
            // Sort by year descending (latest first)
            if (yearB !== yearA) {
                return yearB - yearA;
            }
            
            // If same year, sort by name
            const nameA = (a.model || a.name).toLowerCase();
            const nameB = (b.model || b.name).toLowerCase();
            
            // For same year, put "pro max" before "pro" before "plus" before base model
            const getPriority = (name) => {
                if (name.includes('pro max')) return 4;
                if (name.includes('pro')) return 3;
                if (name.includes('plus')) return 2;
                if (name.includes('max')) return 2;
                return 1;
            };
            
            const priorityA = getPriority(nameA);
            const priorityB = getPriority(nameB);
            
            if (priorityB !== priorityA) {
                return priorityB - priorityA;
            }
            
            return nameA.localeCompare(nameB);
        });
    }, [getPhoneReleaseYear]);

    // Fetch phones from API based on brand
    useEffect(() => {
        const fetchPhones = async () => {
            setLoading(true);
            setError(null);
            try {
                const dbBrand = getPhoneDbBrand(brandUrlParam);
                const response = await fetch('/api/phones');
                
                if (!response.ok) {
                    throw new Error(`Failed to fetch phones: ${response.status}`);
                }
                
                const data = await response.json();
                
                // Filter phones by brand and map to expected structure
                const brandPhones = data
                    .filter(phone => phone.brand.toLowerCase() === dbBrand.toLowerCase())
                    .map(phone => ({
                        name: phone.model,
                        model: phone.model,
                        brand: phone.brand,
                        image: phone.image || '',
                        repairs: phone.repairs || []
                    }));
                
                console.log('Raw brandPhones before sorting:', brandPhones.map(p => p.model));
                console.log('Looking for iPhone 15:', brandPhones.find(p => p.model.toLowerCase() === 'iphone 15'));
                
                // Sort phones by year (latest first)
                const sortedPhones = sortPhonesByYear(brandPhones);
                console.log('Sorted phones:', sortedPhones.map(p => p.model));
                setModels(sortedPhones);
            } catch (err) {
                console.error('Error fetching phones:', err);
                setError(err.message || 'Failed to load phones');
                setModels([]);
            } finally {
                setLoading(false);
            }
        };

        if (brandUrlParam) {
            fetchPhones();
        }
    }, [brandUrlParam, getPhoneDbBrand, sortPhonesByYear]);

    // Filter models based on search term
    const filteredModels = useMemo(() => {
        if (!searchTerm.trim()) return models;
        
        const searchLower = searchTerm.toLowerCase();
        const filteredModels = models.filter(model => 
            model.name.toLowerCase().includes(searchLower)
        );
        
        // Re-sort filtered results to maintain latest-to-oldest order
        return sortPhonesByYear(filteredModels);
    }, [models, searchTerm, sortPhonesByYear]);

    // Group Samsung models by series
    const groupedModels = useMemo(() => {
        if (brandUrlParam?.toLowerCase() === 'samsung') {
            const groups = {
                'S Series': [],
                'A Series': [],
                'Z Flip': [],
                'Z Fold': [],
                'Note': [],
                'J Series': [],
                'Others': []
            };
            
            const modelsToGroup = searchTerm.trim() 
                ? models.filter(model => model.name.toLowerCase().includes(searchTerm.toLowerCase()))
                : models;
                
            modelsToGroup.forEach(model => {
                const lowerName = model.name.toLowerCase();
                if (lowerName.includes('z flip')) {
                    groups['Z Flip'].push(model);
                } else if (lowerName.includes('z fold')) {
                    groups['Z Fold'].push(model);
                } else if (lowerName.includes('note')) {
                    groups['Note'].push(model);
                } else if (lowerName.includes('galaxy j')) {
                    groups['J Series'].push(model);
                } else if (lowerName.includes(' a') || lowerName.startsWith('samsung a')) {
                    groups['A Series'].push(model);
                } else if (lowerName.includes('galaxy s')) {
                    groups['S Series'].push(model);
                } else {
                    groups['Others'].push(model);
                }
            });
            
            // Sort each group from latest to oldest
            Object.keys(groups).forEach(groupName => {
                groups[groupName] = sortPhonesByYear(groups[groupName]);
            });
            
            return groups;
        }
        return null;
    }, [brandUrlParam, models, searchTerm, sortPhonesByYear]);

    useEffect(() => {
        setSelectedPhone(null);
        setShowRepairsModal(false);
        setIsLoadingRepairs(false);
        setFetchError(null);
        setRepairsData([]);
        setSearchTerm('');
        setHasOpenedModalFromUrl(false); // Reset the flag when brand changes
    }, [brandUrlParam]);

    const handleModelClick = useCallback(async (model) => {
        if (!model || isLoadingRepairs) return;
        setSelectedPhone(model);
        setIsLoadingRepairs(true);
        setFetchError(null);
        setRepairsData([]);
        setShowRepairsModal(true);
        
        try {
            // Use the repairs data that came with the phone
            if (model.repairs && model.repairs.length > 0) {
                setRepairsData(model.repairs);
            } else {
                // Fallback to API call if repairs not included
                const dbBrand = model.brand || getPhoneDbBrand(brandUrlParam);
                const url = `/api/phones/repairs?brand=${dbBrand}&model=${encodeURIComponent(model.name)}`;
                console.log("Request URL:", url);
                const response = await fetch(url);
                if (!response.ok) throw new Error(`Server error: ${response.status} ${response.statusText}`);
                const data = await response.json();
                setRepairsData(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error fetching phone repairs data:', error);
            setFetchError(error.message || 'Failed to load repair details.');
            setRepairsData([]);
        } finally {
            setIsLoadingRepairs(false);
        }
    }, [isLoadingRepairs, brandUrlParam, getPhoneDbBrand]);

    const handleModelKeyDown = useCallback((event, model) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleModelClick(model);
        }
    }, [handleModelClick]);

    const handleCloseModal = useCallback(() => {
        setShowRepairsModal(false);
    }, []);
    
    // Check for model query parameter and auto-open modal
    // This useEffect must be after handleModelClick is defined
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const modelParam = params.get('model');
        
        console.log('Model search param:', modelParam);
        console.log('Models loaded:', models.length);
        console.log('isLoadingRepairs:', isLoadingRepairs);
        console.log('hasOpenedModalFromUrl:', hasOpenedModalFromUrl);
        
        // Only process if we haven't already opened the modal from URL
        if (modelParam && models.length > 0 && !isLoadingRepairs && !hasOpenedModalFromUrl) {
            // Mark that we're processing the URL parameter
            setHasOpenedModalFromUrl(true);
            
            // Clear the query parameter immediately to prevent re-opening
            const newParams = new URLSearchParams(location.search);
            newParams.delete('model');
            const newSearch = newParams.toString();
            const newUrl = location.pathname + (newSearch ? `?${newSearch}` : '');
            window.history.replaceState({}, '', newUrl);
            // Normalize the search query to match database format
            // Convert "iPhone 15 Plus" to "iPhone 15 plus" to match DB
            const normalizedParam = modelParam
                .replace(/\bPlus\b/gi, 'plus')
                .replace(/\bPro\b/gi, 'pro')
                .replace(/\bMax\b/gi, 'max')
                .replace(/\bMini\b/gi, 'mini')
                .replace(/\bUltra\b/gi, 'ultra')
                .replace(/\bLite\b/gi, 'lite')
                .replace(/\bEdge\b/gi, 'edge')
                .replace(/\bNote\b/gi, 'note')
                .replace(/\bFE\b/gi, 'fe')
                .replace(/\b5G\b/gi, '5g')
                .replace(/\b4G\b/gi, '4g')
                .toLowerCase();
            
            console.log('Normalized search:', normalizedParam);
            console.log('Available models:', models.length, 'phones');
            
            // Find the model that matches the query parameter - EXACT MATCH ONLY
            const matchingModel = models.find(model => {
                const modelName = (model.name || model.model || '').toLowerCase().trim();
                const searchTerm = normalizedParam.trim();
                
                // EXACT MATCH ONLY - case insensitive
                if (modelName === searchTerm) {
                    console.log('✓ Exact match found:', modelName);
                    return true;
                }
                
                return false;
            });
            
            if (matchingModel) {
                console.log('✅ Opening modal for:', matchingModel.name);
                // Small delay to ensure component is ready
                setTimeout(() => {
                    handleModelClick(matchingModel);
                }, 100);
            } else {
                // Show "Device not found" message  
                console.log('❌ No exact match found for:', normalizedParam);
                
                // Show error notification
                const notification = document.createElement('div');
                notification.style.cssText = `
                    position: fixed;
                    top: 20px;
                    left: 50%;
                    transform: translateX(-50%);
                    background: #dc3545;
                    color: white;
                    padding: 15px 25px;
                    border-radius: 8px;
                    z-index: 10000;
                    font-size: 14px;
                    box-shadow: 0 4px 12px rgba(0,0,0,0.15);
                    animation: slideDown 0.3s ease;
                `;
                notification.innerHTML = `
                    <strong>❌ Device not found</strong><br>
                    "${modelParam}" is not available
                `;
                document.body.appendChild(notification);
                
                // Add animation
                const style = document.createElement('style');
                style.textContent = `
                    @keyframes slideDown {
                        from { transform: translate(-50%, -100%); opacity: 0; }
                        to { transform: translate(-50%, 0); opacity: 1; }
                    }
                `;
                document.head.appendChild(style);
                
                // Remove notification after 5 seconds
                setTimeout(() => {
                    notification.style.animation = 'slideDown 0.3s ease reverse';
                    setTimeout(() => {
                        document.body.removeChild(notification);
                        document.head.removeChild(style);
                    }, 300);
                }, 5000);
            }
        }
    }, [models, location.search, handleModelClick, isLoadingRepairs, hasOpenedModalFromUrl]);

    const getBrandDisplayName = (brand) => {
        const brandMap = {
            'iphone': 'iPhone',
            'samsung': 'Samsung',
            'pixel': 'Google Pixel',
            'oneplus': 'OnePlus',
            'huawei': 'Huawei',
            'nokia': 'Nokia',
            'motorola': 'Motorola',
            'oppo': 'Oppo',
            'redmi': 'Redmi',
            'xiaomi': 'Xiaomi/Redmi',
            'vivo': 'Vivo',
            'realme': 'Realme',
            'honor': 'Honor',
            'lg': 'LG',
            'sony': 'Sony',
            'asus': 'Asus',
            'htc': 'HTC'
        };
        return brandMap[brand?.toLowerCase()] || brand;
    };

    const brandDisplayName = getBrandDisplayName(brandUrlParam);

    if (loading) {
        return (
            <div style={styles.loadingContainer}>
                <div style={styles.spinner}></div>
                <p>Loading {brandDisplayName} models...</p>
            </div>
        );
    }

    if (error) {
        return (
            <div style={styles.errorContainer}>
                <p style={styles.errorText}>Error: {error}</p>
            </div>
        );
    }

    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Select Your {brandDisplayName} Model</h1>
            </div>

            {/* Search Bar */}
            <div style={styles.searchContainer}>
                <FaSearch style={styles.searchIcon} />
                <input
                    type="text"
                    placeholder={`Search ${brandDisplayName} models...`}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={styles.searchInput}
                />
            </div>

            {/* Results count */}
            {searchTerm && (
                <p style={styles.resultsCount}>
                    Found {filteredModels.length} model{filteredModels.length !== 1 ? 's' : ''}
                </p>
            )}

            {/* Models Grid */}
            {groupedModels ? (
                // Grouped display for Samsung
                <div style={styles.groupedContainer}>
                    {Object.entries(groupedModels).map(([groupName, groupModels]) => {
                        if (groupModels.length === 0) return null;
                        return (
                            <div key={groupName} style={styles.groupSection}>
                                <h2 style={styles.groupTitle}>{groupName}</h2>
                                <div style={styles.modelsGrid}>
                                    {groupModels.map((model, index) => (
                                        <div
                                            key={index}
                                            style={styles.modelCard}
                                            onClick={() => handleModelClick(model)}
                                            onKeyDown={(e) => handleModelKeyDown(e, model)}
                                            onMouseEnter={(e) => {
                                                e.currentTarget.style.transform = 'translateY(-5px)';
                                                e.currentTarget.style.boxShadow = '0 5px 15px rgba(0,0,0,0.3)';
                                            }}
                                            onMouseLeave={(e) => {
                                                e.currentTarget.style.transform = 'translateY(0)';
                                                e.currentTarget.style.boxShadow = 'none';
                                            }}
                                            tabIndex={0}
                                            role="button"
                                            aria-label={`Select ${model.name} for repairs`}
                                        >
                                            <img
                                                src={model.image || '/images/phone-placeholder.png'}
                                                alt={model.name}
                                                style={styles.modelImage}
                                                onError={(e) => {
                                                    e.target.src = '/images/phone-placeholder.png';
                                                }}
                                            />
                                            <p style={styles.modelName}>{model.name}</p>
                                            <div style={styles.repairIcon}>
                                                <FaTools />
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                // Regular grid for other brands
                <div style={styles.modelsGrid}>
                    {filteredModels.length > 0 ? (
                        filteredModels.map((model, index) => (
                            <div
                                key={index}
                                style={styles.modelCard}
                                onClick={() => handleModelClick(model)}
                                onKeyDown={(e) => handleModelKeyDown(e, model)}
                                onMouseEnter={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-5px)';
                                    e.currentTarget.style.boxShadow = '0 5px 15px rgba(0,0,0,0.3)';
                                }}
                                onMouseLeave={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = 'none';
                                }}
                                tabIndex={0}
                                role="button"
                                aria-label={`Select ${model.name} for repairs`}
                            >
                                <img
                                    src={model.image || '/images/phone-placeholder.png'}
                                    alt={model.name}
                                    style={styles.modelImage}
                                    onError={(e) => {
                                        e.target.src = '/images/phone-placeholder.png';
                                    }}
                                />
                                <p style={styles.modelName}>{model.name}</p>
                                <div style={styles.repairIcon}>
                                    <FaTools />
                                </div>
                            </div>
                        ))
                    ) : (
                        <p style={styles.noModelsText}>
                            {searchTerm 
                                ? `No ${brandDisplayName} models found matching "${searchTerm}"`
                                : `No ${brandDisplayName} models available`}
                        </p>
                    )}
                </div>
            )}

            {/* Repairs Modal */}
            {showRepairsModal && (
                <RepairsModal
                    show={showRepairsModal}
                    onClose={handleCloseModal}
                    watchModel={{
                        name: selectedPhone?.name || 'Device',
                        repairs: repairsData,
                        image: selectedPhone?.image
                    }}
                />
            )}
        </div>
    );
}

// Styles - Black and White Theme
const styles = {
    container: {
        padding: '20px',
        maxWidth: '1200px',
        margin: '0 auto',
        minHeight: '100vh',
        backgroundColor: '#f8f9fa'
    },
    header: {
        marginBottom: '30px',
        textAlign: 'center'
    },
    backButton: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        color: '#6c757d',
        textDecoration: 'none',
        fontSize: '16px',
        marginBottom: '20px',
        transition: 'opacity 0.3s',
        '&:hover': {
            opacity: '0.7'
        }
    },
    title: {
        fontSize: '2.5em',
        fontWeight: 'bold',
        color: '#6c757d',
        margin: '0'
    },
    searchContainer: {
        position: 'relative',
        maxWidth: '500px',
        margin: '0 auto 30px',
    },
    searchIcon: {
        position: 'absolute',
        left: '15px',
        top: '50%',
        transform: 'translateY(-50%)',
        color: '#6c757d',
        fontSize: '18px'
    },
    searchInput: {
        width: '100%',
        padding: '12px 20px 12px 45px',
        fontSize: '16px',
        border: '2px solid #6c757d',
        borderRadius: '25px',
        outline: 'none',
        transition: 'border-color 0.3s',
        backgroundColor: '#fff',
        color: '#6c757d'
    },
    resultsCount: {
        textAlign: 'center',
        color: '#6c757d',
        marginBottom: '20px',
        fontSize: '14px'
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '400px',
        gap: '20px'
    },
    spinner: {
        width: '50px',
        height: '50px',
        border: '3px solid #ddd',
        borderTop: '3px solid #6c757d',
        borderRadius: '50%',
        animation: 'spin 1s linear infinite'
    },
    errorContainer: {
        textAlign: 'center',
        padding: '40px',
        minHeight: '400px'
    },
    errorText: {
        color: '#6c757d',
        fontSize: '18px',
        marginBottom: '20px'
    },
    backLink: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: '8px',
        color: '#6c757d',
        textDecoration: 'none',
        fontSize: '16px',
        '&:hover': {
            opacity: '0.7'
        }
    },
    groupedContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: '40px'
    },
    groupSection: {
        backgroundColor: '#fff',
        borderRadius: '10px',
        padding: '20px',
        border: '2px solid #6c757d',
        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
    },
    groupTitle: {
        fontSize: '1.8em',
        fontWeight: '600',
        color: '#6c757d',
        marginBottom: '20px',
        paddingBottom: '10px',
        borderBottom: '2px solid #6c757d'
    },
    modelsGrid: {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))',
        gap: '20px',
        justifyContent: 'center'
    },
    modelCard: {
        backgroundColor: '#fff',
        borderRadius: '10px',
        padding: '15px',
        textAlign: 'center',
        cursor: 'pointer',
        transition: 'all 0.3s ease',
        border: '2px solid #6c757d',
        position: 'relative',
        '&:hover': {
            transform: 'translateY(-5px)',
            boxShadow: '0 5px 15px rgba(0,0,0,0.3)'
        }
    },
    modelImage: {
        width: '100px',
        height: '120px',
        objectFit: 'contain',
        marginBottom: '10px'
    },
    modelName: {
        fontSize: '14px',
        fontWeight: '500',
        color: '#6c757d',
        margin: '10px 0 5px'
    },
    repairIcon: {
        position: 'absolute',
        top: '10px',
        right: '10px',
        backgroundColor: '#6c757d',
        color: '#fff',
        borderRadius: '50%',
        width: '30px',
        height: '30px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        opacity: '0.9'
    },
    noModelsText: {
        textAlign: 'center',
        color: '#6c757d',
        fontSize: '18px',
        gridColumn: '1 / -1',
        padding: '40px'
    }
};

// Add keyframes for spinner animation
const styleSheet = document.createElement("style");
styleSheet.textContent = `
    @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
    }
`;
document.head.appendChild(styleSheet);

export default PhoneModels;